import {
  ApiError,
  queryKeys,
  WORKSPACE_POST_BODY_MAX,
  WORKSPACE_REACTION_LABELS,
  WORKSPACE_REACTIONS,
  type WorkspaceDm,
  type WorkspaceDmCandidate,
  type WorkspaceGuest,
  type WorkspaceMessage,
  type WorkspaceReactionSummary,
  type WorkspaceSpace,
  type WorkspaceThread,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CornerDownRight,
  CornerUpLeft,
  Hash,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Pencil,
  Plus,
  SmilePlus,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  AttachButton,
  AttachmentChips,
  AttachmentList,
  inlineImageUploader,
  useFileUploads,
} from '../components/attachments.js'
import { DataTable } from '../components/data-table.js'
import { RichTextEditor } from '../components/rich-text-editor.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/** The red count beside a channel, conversation or tab (t1299); same look as
 *  the sidebar badge. */
function UnreadPill({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span
      role="status"
      aria-label={`${count} unread`}
      className="min-w-4 rounded-full bg-danger px-1 text-center text-2xs font-semibold leading-4 text-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

/**
 * Marks a space read up to its newest message while it is open (t1299). Keyed
 * on that message's id, so opening the space clears it and a message arriving
 * on the next poll clears again; the lists and the sidebar count refetch after.
 */
function useMarkRead(spaceId: string, newestMessageId: string | undefined) {
  const queryClient = useQueryClient()
  const marked = useRef<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: spaceId and the newest id are the triggers
  useEffect(() => {
    if (!newestMessageId) return
    const key = `${spaceId}:${newestMessageId}`
    if (marked.current === key) return
    marked.current = key
    api
      .put(`/workspace/spaces/${spaceId}/read`, { messageId: newestMessageId })
      .then(() =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.workspace.spaces() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.workspace.dms() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.navCounts() }),
        ]),
      )
      .catch(() => {
        // Only a missed clear; allow a retry on the next poll.
        marked.current = null
      })
  }, [spaceId, newestMessageId])
}

type MessagePage = {
  items: WorkspaceMessage[]
  total: number
  page: number
  pageSize: number
  /** Newest message of any kind, replies included (t1363). */
  latestMessageId?: string | null
}

/**
 * The internal staff workspace: channels (running chat) and boards
 * (bulletin-style posts) in their own tabs, private staff-to-staff direct
 * messages (t0954) in another, and — for `workspace:manage` — a Guest access
 * tab for inviting outside collaborators to specific spaces. Mirrors
 * `wiki.tsx`'s shape closely; the two features share the same `guest` role
 * and invite mechanism (see modules/workspace/service.ts). Guests aren't
 * staff, so they never see the Direct messages tab.
 */
export function WorkspacePage() {
  const { staff, can } = useAuth()
  const canManage = can('workspace:manage')
  const isGuest = staff?.role === 'guest'
  // A push notification links here with ?tab=&space= (t1217).
  const search = useSearch({ strict: false }) as { tab?: string; space?: string; thread?: string }
  const linkedTab = ['channels', 'boards', 'dms'].includes(search.tab ?? '')
    ? search.tab
    : undefined
  // Direct messages come first and are the default (t1386); guests have none.
  const tab =
    linkedTab === 'dms' && isGuest ? 'channels' : (linkedTab ?? (isGuest ? 'channels' : 'dms'))
  const linkedSpace = typeof search.space === 'string' ? search.space : null
  const linkedThread = typeof search.thread === 'string' ? search.thread : null

  // Counts for the tab labels; the same queries (and keys) the tabs run, so
  // React Query shares them.
  const { data: spaceList } = useQuery({
    queryKey: queryKeys.workspace.spaces(),
    queryFn: () => api.get<WorkspaceSpace[]>('/workspace/spaces'),
    refetchInterval: 15_000,
  })
  const { data: dmList } = useQuery({
    queryKey: queryKeys.workspace.dms(),
    queryFn: () => api.get<WorkspaceDm[]>('/workspace/dms'),
    refetchInterval: 15_000,
    enabled: !isGuest,
  })
  const unreadIn = (kind: 'channel' | 'board') =>
    (spaceList ?? [])
      .filter((x) => x.kind === kind && !x.archivedAt)
      .reduce((sum, x) => sum + x.unreadCount, 0)
  const dmUnread = (dmList ?? []).reduce((sum, d) => sum + d.unreadCount, 0)

  return (
    <Page>
      <PageHeader
        title="Workspace"
        description="Private direct messages, internal staff channels, and bulletin boards. A direct message is visible only to the two people in it — not even a manager can see it. Guests see only the channels and boards they're invited to."
      />
      <Tabs key={`${tab}:${linkedSpace}:${linkedThread}`} defaultValue={tab}>
        <TabsList>
          {!isGuest ? (
            <TabsTrigger value="dms" className="gap-1.5">
              Direct messages
              <UnreadPill count={dmUnread} />
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="channels" className="gap-1.5">
            Channels
            <UnreadPill count={unreadIn('channel')} />
          </TabsTrigger>
          <TabsTrigger value="boards" className="gap-1.5">
            Boards
            <UnreadPill count={unreadIn('board')} />
          </TabsTrigger>
          {canManage ? <TabsTrigger value="guests">Guest access</TabsTrigger> : null}
        </TabsList>
        {!isGuest ? (
          <TabsContent value="dms">
            <DmsTab
              initialId={tab === 'dms' ? linkedSpace : null}
              initialThreadId={tab === 'dms' ? linkedThread : null}
            />
          </TabsContent>
        ) : null}
        <TabsContent value="channels">
          <SpacesTab
            kind="channel"
            initialId={tab === 'channels' ? linkedSpace : null}
            initialThreadId={tab === 'channels' ? linkedThread : null}
          />
        </TabsContent>
        <TabsContent value="boards">
          <SpacesTab kind="board" initialId={tab === 'boards' ? linkedSpace : null} />
        </TabsContent>
        {canManage ? (
          <TabsContent value="guests">
            <GuestsTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </Page>
  )
}

// -------------------------------------------------------------------- spaces

function SpacesTab({
  kind,
  initialId,
  initialThreadId = null,
}: {
  kind: 'channel' | 'board'
  initialId: string | null
  initialThreadId?: string | null
}) {
  const { can } = useAuth()
  const canManage = can('workspace:manage')
  const canWrite = can('workspace:write')

  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(initialId)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<WorkspaceSpace | null>(null)

  const { data: allSpaces, isLoading: spacesLoading } = useQuery({
    queryKey: queryKeys.workspace.spaces(),
    queryFn: () => api.get<WorkspaceSpace[]>('/workspace/spaces'),
    refetchInterval: 15_000,
  })
  const spaces = (allSpaces ?? []).filter((s) => s.kind === kind)
  const active = spaces.filter((s) => !s.archivedAt)
  const archived = spaces.filter((s) => s.archivedAt)

  useEffect(() => {
    if (!selectedSpaceId && active.length > 0) setSelectedSpaceId(active[0]?.id ?? null)
  }, [active, selectedSpaceId])

  const selected = spaces.find((s) => s.id === selectedSpaceId) ?? null
  const label = kind === 'channel' ? 'Channel' : 'Board'
  const Icon = kind === 'channel' ? Hash : Megaphone

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
      <div className="rounded-lg border border-border bg-surface p-2">
        <div className="flex items-center justify-between px-2 py-1.5">
          <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
            {label}s
          </p>
          {canManage ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label={`New ${label.toLowerCase()}`}
              onClick={() => setCreating(true)}
            >
              <Plus className="size-3.5" />
            </Button>
          ) : null}
        </div>
        {spacesLoading ? null : active.length > 0 ? (
          <ul className="space-y-0.5">
            {active.map((space) => (
              <li key={space.id}>
                <button
                  type="button"
                  onClick={() => setSelectedSpaceId(space.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors',
                    space.id === selectedSpaceId
                      ? 'bg-sidebar-active text-sidebar-active-foreground'
                      : 'text-foreground hover:bg-surface-sunken',
                  )}
                >
                  <Icon className="size-3.5 shrink-0 text-foreground-muted" aria-hidden="true" />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate',
                      space.unreadCount > 0 && space.id !== selectedSpaceId && 'font-semibold',
                    )}
                  >
                    {space.name}
                  </span>
                  {space.id !== selectedSpaceId ? <UnreadPill count={space.unreadCount} /> : null}
                  {canManage && space.memberCount > 0 ? (
                    <span className="text-2xs tabular text-foreground-muted">
                      {space.memberCount}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2 py-4 text-center text-xs text-foreground-muted">
            {canManage
              ? `Create a ${label.toLowerCase()} to start.`
              : `No ${label.toLowerCase()}s yet.`}
          </p>
        )}
        {canManage && archived.length > 0 ? (
          <details className="mt-2 px-2">
            <summary className="cursor-pointer text-2xs text-foreground-muted">
              Archived ({archived.length})
            </summary>
            <ul className="mt-1 space-y-0.5">
              {archived.map((space) => (
                <li key={space.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedSpaceId(space.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm text-foreground-muted transition-colors hover:bg-surface-sunken',
                      space.id === selectedSpaceId &&
                        'bg-sidebar-active text-sidebar-active-foreground',
                    )}
                  >
                    <Icon className="size-3.5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{space.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {selected ? (
              <>
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-sm font-semibold">{selected.name}</h2>
                  {selected.archivedAt ? <Badge tone="neutral">Archived</Badge> : null}
                </div>
                {selected.description ? (
                  <p className="text-xs text-foreground-muted">{selected.description}</p>
                ) : null}
              </>
            ) : null}
          </div>
          {selected && canManage ? (
            <Button variant="secondary" size="sm" onClick={() => setEditing(selected)}>
              Edit {label.toLowerCase()}
            </Button>
          ) : null}
        </div>

        {selected ? (
          kind === 'channel' ? (
            <ChannelPane
              space={selected}
              canWrite={canWrite && !selected.archivedAt}
              initialThreadId={selected.id === initialId ? initialThreadId : null}
            />
          ) : (
            <BoardPane
              space={selected}
              canWrite={canWrite && !selected.archivedAt}
              canManage={canManage}
            />
          )
        ) : (
          <EmptyState
            icon={Icon}
            title={`No ${label.toLowerCase()} selected`}
            description={`Choose a ${label.toLowerCase()} on the left, or create one.`}
          />
        )}
      </div>

      <CreateSpaceDialog
        open={creating}
        onOpenChange={setCreating}
        kind={kind}
        onCreated={setSelectedSpaceId}
      />
      <EditSpaceDialog space={editing} onClose={() => setEditing(null)} />
    </div>
  )
}

function CreateSpaceDialog({
  open,
  onOpenChange,
  kind,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  kind: 'channel' | 'board'
  onCreated: (id: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const reset = () => {
    setName('')
    setDescription('')
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<WorkspaceSpace>('/workspace/spaces', {
        kind,
        name,
        description: description || undefined,
      }),
    onSuccess: async (space) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.spaces() })
      toast.success(`${kind === 'channel' ? 'Channel' : 'Board'} created`)
      onOpenChange(false)
      onCreated(space.id)
      reset()
    },
    onError: (error: Error) =>
      toast.error('Could not create it', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New {kind === 'channel' ? 'channel' : 'board'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Description" hint="Shown to anyone who can see this space.">
            {(props) => (
              <Textarea
                {...props}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditSpaceDialog({
  space,
  onClose,
}: {
  space: WorkspaceSpace | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    setName(space?.name ?? '')
    setDescription(space?.description ?? '')
  }, [space])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/workspace/spaces/${space?.id}`, { name, description: description || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.spaces() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const setArchived = useMutation({
    mutationFn: (archived: boolean) => api.patch(`/workspace/spaces/${space?.id}`, { archived }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.spaces() })
      toast.success(space?.archivedAt ? 'Unarchived' : 'Archived')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not change that', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={space !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {space?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Description">
            {(props) => (
              <Textarea
                {...props}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter className="justify-between">
          <Button
            variant="ghost"
            onClick={() => setArchived.mutate(!space?.archivedAt)}
            disabled={setArchived.isPending}
          >
            {space?.archivedAt ? 'Unarchive' : 'Archive'}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !name.trim()}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ----------------------------------------------------------------- reactions

/**
 * Quick reactions (t1222): one per person per message — picking another
 * replaces yours, picking your own again removes it. Only reactions somebody
 * has picked are shown as pills (yours carries an ×); the five options live
 * behind a single "add reaction" button that appears on hover (always on a
 * touch screen).
 */
function ReactionBar({
  messageId,
  reactions,
  canWrite,
  onChanged,
  actions,
  className,
}: {
  className?: string
  messageId: string
  reactions: WorkspaceReactionSummary[]
  canWrite: boolean
  /** Extra controls (reply / edit / delete) shown at the end of the row. */
  actions?: ReactNode
  /** Lets a caller holding a snapshot of the message (the board post dialog) adopt the result. */
  onChanged?: (reactions: WorkspaceReactionSummary[]) => void
}) {
  const { staff } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()

  const toggle = useMutation({
    mutationFn: ({
      reaction,
      mine,
    }: {
      reaction: (typeof WORKSPACE_REACTIONS)[number]
      mine: boolean
    }) =>
      mine
        ? api.delete<{ reactions: WorkspaceReactionSummary[] }>(
            `/workspace/messages/${messageId}/reaction`,
          )
        : api.put<{ reactions: WorkspaceReactionSummary[] }>(
            `/workspace/messages/${messageId}/reaction`,
            { reaction },
          ),
    onSuccess: async (result) => {
      onChanged?.(result.reactions)
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
    },
    onError: (error: Error) =>
      toast.error('Could not react', error instanceof ApiError ? error.message : undefined),
  })

  const picked = WORKSPACE_REACTIONS.filter((reaction) =>
    reactions.some((r) => r.reaction === reaction && r.userIds.length > 0),
  )
  if (picked.length === 0 && !canWrite && !actions) return null
  const isMine = (reaction: (typeof WORKSPACE_REACTIONS)[number]) =>
    Boolean(staff && reactions.find((r) => r.reaction === reaction)?.userIds.includes(staff.id))

  return (
    <div className={cn('mt-1 flex flex-wrap items-center gap-1', className)}>
      {picked.map((reaction) => {
        const found = reactions.find((r) => r.reaction === reaction)
        const mine = isMine(reaction)
        const label = WORKSPACE_REACTION_LABELS[reaction]
        return (
          <button
            key={reaction}
            type="button"
            disabled={!canWrite || toggle.isPending}
            title={mine ? 'You reacted — click to remove' : found?.names.join(', ')}
            aria-label={mine ? `${label} (yours) — remove` : label}
            aria-pressed={mine}
            onClick={() => toggle.mutate({ reaction, mine })}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs',
              mine
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-surface-sunken text-foreground',
            )}
          >
            {label} {found?.userIds.length ?? 0}
            {mine ? <X className="size-3" aria-hidden="true" /> : null}
          </button>
        )
      })}
      {canWrite ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Add reaction"
              disabled={toggle.isPending}
              className="inline-flex size-6 items-center justify-center rounded-full border border-border text-foreground-muted hover:text-foreground focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <SmilePlus className="size-3.5" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {WORKSPACE_REACTIONS.map((reaction) => {
              const mine = isMine(reaction)
              return (
                <DropdownMenuCheckboxItem
                  key={reaction}
                  checked={mine}
                  onSelect={() => toggle.mutate({ reaction, mine })}
                >
                  {WORKSPACE_REACTION_LABELS[reaction]}
                </DropdownMenuCheckboxItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {actions}
    </div>
  )
}

// ------------------------------------------------------------------ channels

function ChannelPane({
  space,
  canWrite,
  dm = false,
  initialThreadId = null,
}: {
  space: Pick<WorkspaceSpace, 'id' | 'name'>
  canWrite: boolean
  /** Thread to open on arrival, from a reply alert's link (t1363). */
  initialThreadId?: string | null
  /** True for a direct-message conversation — same message list and
   *  composer as a channel, but delete is author-only (no `workspace:manage`
   *  override; see `deleteMessage` in modules/workspace/service.ts) and the
   *  composer placeholder drops the `#` a channel name gets. */
  dm?: boolean
}) {
  const { staff, can } = useAuth()
  const canManage = !dm && can('workspace:manage')
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  // The message whose thread is open in the side panel (t1363).
  const [threadId, setThreadId] = useState<string | null>(initialThreadId)
  const threadSpace = useRef(space.id)
  if (threadSpace.current !== space.id) {
    threadSpace.current = space.id
    if (threadId !== null) setThreadId(null)
  }
  // The message being edited in the composer (t1364); the unsent draft is
  // set aside meanwhile and put back afterwards.
  const [editingMsg, setEditingMsg] = useState<WorkspaceMessage | null>(null)
  const stashedDraft = useRef('')
  const composerRef = useRef<HTMLDivElement>(null)
  const uploads = useFileUploads({ endpoint: '/attachments?for=workspace' })
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  // True while the reader is at (or near) the newest message; only then do
  // polled/arriving messages and late-loading images pull the view down (t1362).
  const atBottom = useRef(true)
  const openedSpace = useRef<string | null>(null)
  const lastNewestId = useRef<string | undefined>(undefined)
  const loadedPages = useRef(0)
  // Distance from the bottom when "Load earlier" was clicked, to keep position.
  const keepFromBottom = useRef<number | null>(null)
  const toBottom = () => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }
  // Replies live in their message's thread, not in this list (t1363).
  const filters = { topLevel: true }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.workspace.messages(space.id, filters),
    queryFn: ({ pageParam }) =>
      api.get<MessagePage>(`/workspace/spaces/${space.id}/messages`, {
        query: { ...filters, page: pageParam, pageSize: 50 },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    refetchInterval: 15_000,
  })
  // The API returns newest first (for pagination); a chat reads oldest to
  // newest, so the whole flattened set is reversed for display.
  const items = [...(data?.pages.flatMap((p) => p.items) ?? [])].reverse()
  const newestId = data?.pages[0]?.items[0]?.id
  const pageCount = data?.pages.length ?? 0
  // Read up to the newest message of any kind: the newest reply is not in this list.
  useMarkRead(space.id, data?.pages[0]?.latestMessageId ?? newestId)

  // Land on the newest message when a conversation is opened or switched to;
  // afterwards follow new messages only if the reader is already at the bottom,
  // and keep their place when earlier messages are prepended (t1362).
  // biome-ignore lint/correctness/useExhaustiveDependencies: refs and toBottom are stable
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || isLoading) return
    if (openedSpace.current !== space.id) {
      openedSpace.current = space.id
      atBottom.current = true
      keepFromBottom.current = null
      toBottom()
    } else if (keepFromBottom.current !== null && pageCount > loadedPages.current) {
      el.scrollTop = el.scrollHeight - keepFromBottom.current
      keepFromBottom.current = null
    } else if (newestId !== lastNewestId.current && atBottom.current) {
      toBottom()
    }
    lastNewestId.current = newestId
    loadedPages.current = pageCount
  }, [space.id, isLoading, newestId, pageCount])

  // Images/attachments growing after the jump must not leave the view short of
  // the bottom.
  // biome-ignore lint/correctness/useExhaustiveDependencies: toBottom is stable
  useEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (atBottom.current && keepFromBottom.current === null) toBottom()
    })
    ro.observe(content)
    return () => ro.disconnect()
  }, [])

  const send = useMutation({
    mutationFn: (body: string) =>
      api.post(`/workspace/spaces/${space.id}/messages`, {
        body,
        attachmentIds: uploads.ids,
      }),
    onSuccess: async () => {
      setDraft('')
      uploads.reset()
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
      atBottom.current = true
      requestAnimationFrame(toBottom)
    },
    onError: (error: Error) =>
      toast.error('Could not send', error instanceof ApiError ? error.message : undefined),
  })

  const startEdit = (m: WorkspaceMessage) => {
    if (!editingMsg) stashedDraft.current = draft
    setEditingMsg(m)
    setDraft(m.body)
    requestAnimationFrame(() => composerRef.current?.querySelector('textarea')?.focus())
  }
  const cancelEdit = () => {
    setEditingMsg(null)
    setDraft(stashedDraft.current)
    stashedDraft.current = ''
  }
  const edit = useMutation({
    mutationFn: (body: string) => api.patch(`/workspace/messages/${editingMsg?.id}`, { body }),
    onSuccess: async () => {
      setEditingMsg(null)
      setDraft(stashedDraft.current)
      stashedDraft.current = ''
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/workspace/messages/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() }),
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  const submit = () => {
    const body = draft.trim()
    if (editingMsg) {
      if (edit.isPending || (!body && editingMsg.attachments.length === 0)) return
      if (body === editingMsg.body) return cancelEdit()
      return edit.mutate(body)
    }
    if (send.isPending || uploads.uploading) return
    if (body || uploads.ids.length > 0) send.mutate(body)
  }

  return (
    <div className="relative flex h-[calc(100vh-280px)] min-h-96 overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scrollbar-thin p-4"
          onScroll={(e) => {
            const el = e.currentTarget
            atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
          }}
        >
          <div ref={contentRef} className="space-y-3">
            {hasNextPage ? (
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const el = scrollRef.current
                    if (el) keepFromBottom.current = el.scrollHeight - el.scrollTop
                    void fetchNextPage()
                  }}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load earlier messages'}
                </Button>
              </div>
            ) : null}
            {isLoading ? null : items.length === 0 ? (
              <EmptyState icon={MessagesSquare} title="No messages yet" description="Say hello." />
            ) : (
              items.map((m) => (
                <MessageItem
                  key={m.id}
                  m={m}
                  mine={m.authorUserId === staff?.id}
                  canWrite={canWrite}
                  canEdit={canWrite && m.authorUserId === staff?.id}
                  canDelete={m.authorUserId === staff?.id || canManage}
                  onEdit={() => startEdit(m)}
                  onDelete={() => remove.mutate(m.id)}
                  onOpenThread={() => setThreadId(m.id)}
                />
              ))
            )}
          </div>
        </div>
        {canWrite ? (
          <div className="border-t border-border p-3">
            {editingMsg ? (
              <div className="mb-2 flex items-center gap-2 border-l-2 border-primary pl-2 text-xs text-foreground-muted">
                <span className="min-w-0 flex-1 truncate">Editing message</span>
                <Button variant="ghost" size="icon" aria-label="Cancel edit" onClick={cancelEdit}>
                  <X className="size-3.5" />
                </Button>
              </div>
            ) : (
              <AttachmentChips uploads={uploads} />
            )}
            <div className="flex items-end gap-2">
              {editingMsg ? null : <AttachButton uploads={uploads} label="Attach" />}
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={uploads.handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && editingMsg) cancelEdit()
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    submit()
                  }
                }}
                placeholder={dm ? `Message ${space.name}` : `Message #${space.name}`}
                rows={1}
                className="flex-1 resize-none"
              />
              <Button
                variant="primary"
                onClick={submit}
                disabled={
                  editingMsg
                    ? edit.isPending || (!draft.trim() && editingMsg.attachments.length === 0)
                    : send.isPending ||
                      uploads.uploading ||
                      (!draft.trim() && uploads.ids.length === 0)
                }
              >
                {editingMsg ? 'Save' : 'Send'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      {threadId ? (
        <ThreadPanel
          key={threadId}
          spaceId={space.id}
          messageId={threadId}
          canWrite={canWrite}
          canManage={canManage}
          onClose={() => setThreadId(null)}
        />
      ) : null}
    </div>
  )
}

/** One chat message with its actions. In the main list it shows a reply count
 *  that opens the thread; inside a thread (`onOpenThread` unset) it does not. */
function MessageItem({
  m,
  mine,
  canWrite,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onOpenThread,
}: {
  m: WorkspaceMessage
  /** Written by the signed-in user: shown on the right in a tinted bubble. */
  mine: boolean
  canWrite: boolean
  canEdit: boolean
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
  onOpenThread?: () => void
}) {
  const actionClass =
    'size-6 text-foreground-muted transition-opacity hover:text-foreground focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100'
  const showReply = canWrite && Boolean(onOpenThread)
  const actions =
    showReply || canEdit || canDelete ? (
      <>
        {showReply ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Reply"
            title="Reply"
            className={actionClass}
            onClick={onOpenThread}
          >
            <CornerUpLeft className="size-3.5" />
          </Button>
        ) : null}
        {canEdit ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Edit message"
            title="Edit"
            className={actionClass}
            onClick={onEdit}
          >
            <Pencil className="size-3.5" />
          </Button>
        ) : null}
        {canDelete ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete message"
            title="Delete"
            className={actionClass}
            onClick={onDelete}
          >
            <Trash2 className="size-3.5" />
          </Button>
        ) : null}
      </>
    ) : null

  const side = mine ? 'items-end' : 'items-start'
  return (
    <div id={`msg-${m.id}`} className={cn('group flex min-w-0 flex-col', side)}>
      <div className="flex items-baseline gap-2 px-1">
        {mine ? null : <span className="text-sm font-medium">{m.authorName ?? 'Unknown'}</span>}
        <span className="text-2xs text-foreground-muted">
          {new Date(m.createdAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
        {m.editedAt ? (
          <span
            className="text-2xs text-foreground-muted"
            title={`Edited ${new Date(m.editedAt).toLocaleString('en-US')}`}
          >
            (edited)
          </span>
        ) : null}
      </div>
      {m.body || m.attachments.length > 0 ? (
        <div
          className={cn(
            'max-w-[85%] min-w-0 rounded-2xl px-3 py-2 text-sm',
            mine ? 'rounded-br-sm bg-primary-subtle' : 'rounded-bl-sm bg-surface-sunken',
          )}
        >
          {m.body ? <p className="whitespace-pre-wrap break-words">{m.body}</p> : null}
          <AttachmentList files={m.attachments} compact />
        </div>
      ) : null}
      <ReactionBar
        messageId={m.id}
        reactions={m.reactions}
        canWrite={canWrite}
        actions={actions}
        className={mine ? 'justify-end' : undefined}
      />
      {onOpenThread && m.replyCount > 0 ? (
        <button
          type="button"
          title="Open thread"
          className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          onClick={onOpenThread}
        >
          <CornerDownRight className="size-3" />
          {m.replyCount === 1 ? '1 reply' : `${m.replyCount} replies`}
          {m.lastReplyAt ? (
            <span className="font-normal text-foreground-muted">
              · last reply{' '}
              {new Date(m.lastReplyAt).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          ) : null}
        </button>
      ) : null}
    </div>
  )
}

/**
 * The side panel for one message's thread (t1363): the original, every reply
 * oldest first, and a box that posts a reply to it.
 */
function ThreadPanel({
  spaceId,
  messageId,
  canWrite,
  canManage,
  onClose,
}: {
  spaceId: string
  messageId: string
  canWrite: boolean
  canManage: boolean
  onClose: () => void
}) {
  const { staff } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [editingMsg, setEditingMsg] = useState<WorkspaceMessage | null>(null)
  const stashedDraft = useRef('')
  const composerRef = useRef<HTMLDivElement>(null)
  const uploads = useFileUploads({ endpoint: '/attachments?for=workspace' })
  const scrollRef = useRef<HTMLDivElement>(null)
  const replyCount = useRef(-1)

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.workspace.thread(messageId),
    queryFn: () => api.get<WorkspaceThread>(`/workspace/messages/${messageId}/thread`),
    refetchInterval: 15_000,
    retry: false,
  })

  // The original was deleted (or is no longer visible): nothing left to show.
  useEffect(() => {
    if (isError) onClose()
  }, [isError, onClose])

  // Land on the newest reply when the thread opens or a reply arrives.
  const count = data?.replies.length ?? 0
  // biome-ignore lint/correctness/useExhaustiveDependencies: count is the trigger
  useLayoutEffect(() => {
    if (!data || count === replyCount.current) return
    replyCount.current = count
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [data, count])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editingMsg) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, editingMsg])

  const send = useMutation({
    mutationFn: (body: string) =>
      api.post(`/workspace/spaces/${spaceId}/messages`, {
        body,
        replyToMessageId: messageId,
        attachmentIds: uploads.ids,
      }),
    onSuccess: async () => {
      setDraft('')
      uploads.reset()
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
    },
    onError: (error: Error) =>
      toast.error('Could not send', error instanceof ApiError ? error.message : undefined),
  })

  const edit = useMutation({
    mutationFn: (body: string) => api.patch(`/workspace/messages/${editingMsg?.id}`, { body }),
    onSuccess: async () => {
      setEditingMsg(null)
      setDraft(stashedDraft.current)
      stashedDraft.current = ''
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/workspace/messages/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() }),
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  const startEdit = (m: WorkspaceMessage) => {
    if (!editingMsg) stashedDraft.current = draft
    setEditingMsg(m)
    setDraft(m.body)
    requestAnimationFrame(() => composerRef.current?.querySelector('textarea')?.focus())
  }
  const cancelEdit = () => {
    setEditingMsg(null)
    setDraft(stashedDraft.current)
    stashedDraft.current = ''
  }
  const submit = () => {
    const body = draft.trim()
    if (editingMsg) {
      if (edit.isPending || (!body && editingMsg.attachments.length === 0)) return
      if (body === editingMsg.body) return cancelEdit()
      return edit.mutate(body)
    }
    if (send.isPending || uploads.uploading) return
    if (body || uploads.ids.length > 0) send.mutate(body)
  }

  const item = (m: WorkspaceMessage, isParent: boolean) => (
    <MessageItem
      key={m.id}
      m={m}
      mine={m.authorUserId === staff?.id}
      canWrite={canWrite}
      canEdit={canWrite && m.authorUserId === staff?.id}
      canDelete={m.authorUserId === staff?.id || canManage}
      onEdit={() => startEdit(m)}
      onDelete={() => {
        remove.mutate(m.id)
        if (isParent) onClose()
      }}
    />
  )

  return (
    <aside
      aria-label="Thread"
      className="absolute inset-0 z-10 flex flex-col bg-surface md:static md:inset-auto md:w-96 md:shrink-0 md:border-l md:border-border"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <h3 className="flex-1 text-sm font-semibold">Thread</h3>
        <Button variant="ghost" size="icon" aria-label="Close thread" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto scrollbar-thin p-4">
        {isLoading || !data ? null : (
          <>
            {item(data.parent, true)}
            <div className="flex items-center gap-2 text-xs text-foreground-muted">
              <span>
                {data.replies.length === 1 ? '1 reply' : `${data.replies.length} replies`}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
            {data.replies.map((r) => item(r, false))}
          </>
        )}
      </div>
      {canWrite ? (
        <div className="border-t border-border p-3">
          {editingMsg ? (
            <div className="mb-2 flex items-center gap-2 border-l-2 border-primary pl-2 text-xs text-foreground-muted">
              <span className="min-w-0 flex-1 truncate">Editing message</span>
              <Button variant="ghost" size="icon" aria-label="Cancel edit" onClick={cancelEdit}>
                <X className="size-3.5" />
              </Button>
            </div>
          ) : (
            <AttachmentChips uploads={uploads} />
          )}
          <div ref={composerRef} className="flex items-end gap-2">
            {editingMsg ? null : <AttachButton uploads={uploads} label="Attach" />}
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPaste={uploads.handlePaste}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && editingMsg) cancelEdit()
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  submit()
                }
              }}
              placeholder="Reply in thread"
              rows={1}
              className="flex-1 resize-none"
            />
            <Button
              variant="primary"
              onClick={submit}
              disabled={
                editingMsg
                  ? edit.isPending || (!draft.trim() && editingMsg.attachments.length === 0)
                  : send.isPending ||
                    uploads.uploading ||
                    (!draft.trim() && uploads.ids.length === 0)
              }
            >
              {editingMsg ? 'Save' : 'Reply'}
            </Button>
          </div>
        </div>
      ) : null}
    </aside>
  )
}

// --------------------------------------------------------------------- boards

function BoardPane({
  space,
  canWrite,
  canManage,
}: {
  space: WorkspaceSpace
  canWrite: boolean
  canManage: boolean
}) {
  const { staff } = useAuth()
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<WorkspaceMessage | null>(null)

  const filters = {}
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.workspace.messages(space.id, filters),
    queryFn: ({ pageParam }) =>
      api.get<MessagePage>(`/workspace/spaces/${space.id}/messages`, {
        query: { ...filters, page: pageParam, pageSize: 50 },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    refetchInterval: 15_000,
  })
  const posts = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0
  useMarkRead(space.id, data?.pages[0]?.items[0]?.id)

  const columns: ColumnDef<WorkspaceMessage, unknown>[] = [
    {
      header: 'Title',
      accessorKey: 'title',
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
    },
    {
      header: 'By',
      accessorKey: 'authorName',
      cell: ({ row }) => (
        <span className="text-foreground-muted">{row.original.authorName ?? 'Unknown'}</span>
      ),
    },
    {
      header: 'Posted',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <span className="text-xs text-foreground-muted">
          {new Date(row.original.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      ),
    },
  ]

  return (
    <div>
      {canWrite ? (
        <div className="mb-3 flex justify-end">
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus /> New post
          </Button>
        </div>
      ) : null}
      <DataTable
        data={posts}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setViewing(row)}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          <EmptyState
            icon={Megaphone}
            title="No posts yet"
            description="Publish the first announcement."
            action={canWrite ? { label: 'New post', onClick: () => setCreating(true) } : undefined}
          />
        }
      />
      <CreatePostDialog open={creating} onOpenChange={setCreating} spaceId={space.id} />
      <PostDialog
        message={viewing}
        onClose={() => setViewing(null)}
        canWrite={canWrite}
        canDelete={Boolean(viewing && (viewing.authorUserId === staff?.id || canManage))}
      />
    </div>
  )
}

function CreatePostDialog({
  open,
  onOpenChange,
  spaceId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  spaceId: string
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const uploads = useFileUploads({ endpoint: '/attachments?for=workspace' })

  const reset = () => {
    setTitle('')
    setBody('')
    uploads.reset()
  }

  const create = useMutation({
    mutationFn: () =>
      api.post(`/workspace/spaces/${spaceId}/messages`, {
        title,
        body,
        attachmentIds: uploads.ids,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
      toast.success('Post published')
      onOpenChange(false)
      reset()
    },
    onError: (error: Error) =>
      toast.error('Could not publish', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New post</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Title" required>
            {(props) => (
              <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />
            )}
          </Field>
          <Field label="Content">
            {() => (
              <RichTextEditor
                value={body}
                onChange={(html) => setBody(html ?? '')}
                headings
                links
                maxLength={WORKSPACE_POST_BODY_MAX}
                uploadImage={inlineImageUploader('workspace')}
              />
            )}
          </Field>
          <div className="space-y-2">
            <AttachmentChips uploads={uploads} />
            <AttachButton uploads={uploads} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={
              create.isPending ||
              uploads.uploading ||
              !title.trim() ||
              (!body.trim() && uploads.ids.length === 0)
            }
          >
            {create.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PostDialog({
  message,
  onClose,
  canDelete,
  canWrite,
}: {
  message: WorkspaceMessage | null
  onClose: () => void
  canDelete: boolean
  canWrite: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { staff } = useAuth()
  const [editingPost, setEditingPost] = useState(false)
  const [postTitle, setPostTitle] = useState('')
  const [postBody, setPostBody] = useState('')
  const canEdit = canWrite && message !== null && message.authorUserId === staff?.id
  // The dialog holds a snapshot of the post, so a reaction made here is kept
  // locally too (keyed by post id) until the list refetch replaces the snapshot.
  const [reactionsOverride, setReactionsOverride] = useState<{
    id: string
    reactions: WorkspaceReactionSummary[]
  } | null>(null)
  const reactions =
    reactionsOverride && reactionsOverride.id === message?.id
      ? reactionsOverride.reactions
      : (message?.reactions ?? [])

  const saveEdit = useMutation({
    mutationFn: () =>
      api.patch(`/workspace/messages/${message?.id}`, { title: postTitle, body: postBody }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
      toast.success('Post updated')
      setEditingPost(false)
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/workspace/messages/${message?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.all() })
      toast.success('Post deleted')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog
      open={message !== null}
      onOpenChange={(open) => {
        if (!open) {
          setEditingPost(false)
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingPost ? 'Edit post' : message?.title}</DialogTitle>
        </DialogHeader>
        {editingPost ? (
          <DialogBody className="space-y-4">
            <Field label="Title" required>
              {(props) => (
                <Input
                  {...props}
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                />
              )}
            </Field>
            <Field label="Content">
              {() => (
                <RichTextEditor
                  value={postBody}
                  onChange={(html) => setPostBody(html ?? '')}
                  headings
                  links
                  maxLength={WORKSPACE_POST_BODY_MAX}
                  uploadImage={inlineImageUploader('workspace')}
                />
              )}
            </Field>
          </DialogBody>
        ) : (
          <DialogBody>
            <p className="mb-3 text-xs text-foreground-muted">
              {message?.authorName ?? 'Unknown'} ·{' '}
              {message ? new Date(message.createdAt).toLocaleString('en-US') : ''}
              {message?.editedAt ? ' · (edited)' : ''}
            </p>
            {/* Sanitized server-side (see modules/workspace/content.ts) before it
              ever reaches the database, same guarantee the wiki's content gets. */}
            <div
              className="rich-text text-sm"
              dangerouslySetInnerHTML={{ __html: message?.body ?? '' }}
            />
            <AttachmentList files={message?.attachments} />
            {message ? (
              <div className="group mt-3">
                <ReactionBar
                  messageId={message.id}
                  reactions={reactions}
                  canWrite={canWrite}
                  onChanged={(next) => setReactionsOverride({ id: message.id, reactions: next })}
                />
              </div>
            ) : null}
          </DialogBody>
        )}
        <DialogFooter className="justify-between">
          {canDelete && !editingPost ? (
            <Button variant="ghost" onClick={() => remove.mutate()} disabled={remove.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            {editingPost ? (
              <>
                <Button variant="ghost" onClick={() => setEditingPost(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => saveEdit.mutate()}
                  disabled={
                    saveEdit.isPending ||
                    !postTitle.trim() ||
                    (!postBody.trim() && (message?.attachments.length ?? 0) === 0)
                  }
                >
                  {saveEdit.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                {canEdit ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setPostTitle(message?.title ?? '')
                      setPostBody(message?.body ?? '')
                      setEditingPost(true)
                    }}
                  >
                    Edit
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------------ direct messages

function DmsTab({
  initialId,
  initialThreadId = null,
}: {
  initialId: string | null
  initialThreadId?: string | null
}) {
  const { can } = useAuth()
  const canWrite = can('workspace:write')
  const [selectedDmId, setSelectedDmId] = useState<string | null>(initialId)
  const [starting, setStarting] = useState(false)

  const { data: dms, isLoading } = useQuery({
    queryKey: queryKeys.workspace.dms(),
    queryFn: () => api.get<WorkspaceDm[]>('/workspace/dms'),
    refetchInterval: 15_000,
  })

  useEffect(() => {
    if (!selectedDmId && dms && dms.length > 0) setSelectedDmId(dms[0]?.id ?? null)
  }, [dms, selectedDmId])

  const selected = dms?.find((d) => d.id === selectedDmId) ?? null

  const onOpened = (dm: WorkspaceDm) => setSelectedDmId(dm.id)

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
      <div className="rounded-lg border border-border bg-surface p-2">
        <div className="flex items-center justify-between px-2 py-1.5">
          <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
            Direct messages
          </p>
          {canWrite ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="New message"
              onClick={() => setStarting(true)}
            >
              <Plus className="size-3.5" />
            </Button>
          ) : null}
        </div>
        {isLoading ? null : dms && dms.length > 0 ? (
          <ul className="space-y-0.5">
            {dms.map((dm) => (
              <li key={dm.id}>
                <button
                  type="button"
                  onClick={() => setSelectedDmId(dm.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors',
                    dm.id === selectedDmId
                      ? 'bg-sidebar-active text-sidebar-active-foreground'
                      : 'text-foreground hover:bg-surface-sunken',
                  )}
                >
                  <MessageCircle
                    className="size-3.5 shrink-0 text-foreground-muted"
                    aria-hidden="true"
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate',
                      dm.unreadCount > 0 && dm.id !== selectedDmId && 'font-semibold',
                    )}
                  >
                    {dm.otherUser?.name ?? 'Deleted user'}
                  </span>
                  {dm.id !== selectedDmId ? <UnreadPill count={dm.unreadCount} /> : null}
                  {dm.otherUser && !dm.otherUser.isActive ? (
                    <span className="text-2xs text-foreground-muted">Inactive</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-2 py-4 text-center text-xs text-foreground-muted">
            {canWrite ? 'Start a conversation with a colleague.' : 'No conversations yet.'}
          </p>
        )}
      </div>

      <div>
        {selected ? (
          <>
            <div className="mb-3">
              <h2 className="truncate text-sm font-semibold">
                {selected.otherUser?.name ?? 'Deleted user'}
              </h2>
              <p className="text-xs text-foreground-muted">
                Only the two of you can see this conversation.
              </p>
            </div>
            <ChannelPane
              dm
              space={{ id: selected.id, name: selected.otherUser?.name ?? 'Deleted user' }}
              canWrite={canWrite}
              initialThreadId={selected.id === initialId ? initialThreadId : null}
            />
          </>
        ) : (
          <EmptyState
            icon={MessageCircle}
            title="No conversation selected"
            description="Choose a conversation on the left, or start a new one."
            action={
              canWrite ? { label: 'New message', onClick: () => setStarting(true) } : undefined
            }
          />
        )}
      </div>

      <NewDmDialog open={starting} onOpenChange={setStarting} onOpened={onOpened} />
    </div>
  )
}

function NewDmDialog({
  open,
  onOpenChange,
  onOpened,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpened: (dm: WorkspaceDm) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [userId, setUserId] = useState('')

  const { data: candidates } = useQuery({
    queryKey: queryKeys.workspace.dmCandidates(),
    queryFn: () => api.get<WorkspaceDmCandidate[]>('/workspace/dm-candidates'),
    enabled: open,
  })

  const openMutation = useMutation({
    mutationFn: () => api.post<WorkspaceDm>('/workspace/dms', { userId }),
    onSuccess: async (dm) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.dms() })
      onOpenChange(false)
      onOpened(dm)
      setUserId('')
    },
    onError: (error: Error) =>
      toast.error(
        'Could not start the conversation',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) setUserId('')
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="To" required>
            {(props) => (
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a colleague" />
                </SelectTrigger>
                <SelectContent>
                  {(candidates ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => openMutation.mutate()}
            disabled={openMutation.isPending || !userId}
          >
            {openMutation.isPending ? 'Starting…' : 'Start conversation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// -------------------------------------------------------------------- guests

function toggleInSet(set: Set<string>, id: string): Set<string> {
  const next = new Set(set)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

function SpaceCheckboxList({
  spaces,
  selected,
  onToggle,
}: {
  spaces: WorkspaceSpace[]
  selected: Set<string>
  onToggle: (id: string) => void
}) {
  if (spaces.length === 0) {
    return (
      <p className="text-xs text-foreground-muted">
        No channels or boards exist yet — create one first.
      </p>
    )
  }
  return (
    <ul className="max-h-48 space-y-1 overflow-y-auto scrollbar-thin rounded-md border border-border p-2">
      {spaces.map((space) => (
        <li key={space.id}>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-3.5 accent-[var(--primary)]"
              checked={selected.has(space.id)}
              onChange={() => onToggle(space.id)}
            />
            {space.kind === 'channel' ? '#' : '📌'} {space.name}
          </label>
        </li>
      ))}
    </ul>
  )
}

function GuestsTab() {
  const toast = useToast()
  const [inviting, setInviting] = useState(false)
  const [editingGuest, setEditingGuest] = useState<WorkspaceGuest | null>(null)

  const { data: spaces } = useQuery({
    queryKey: queryKeys.workspace.spaces(),
    queryFn: () => api.get<WorkspaceSpace[]>('/workspace/spaces'),
  })
  const { data: guests, isLoading } = useQuery({
    queryKey: queryKeys.workspace.guests(),
    queryFn: () => api.get<WorkspaceGuest[]>('/workspace/guests'),
  })

  const spaceName = (id: string) => spaces?.find((s) => s.id === id)?.name ?? 'Unknown space'

  const resend = useMutation({
    mutationFn: (id: string) => api.post(`/workspace/guests/${id}/invite`, {}),
    onSuccess: () => toast.success('Invite email resent'),
    onError: (error: Error) =>
      toast.error(
        'Could not resend the invite',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const columns: ColumnDef<WorkspaceGuest, unknown>[] = [
    { header: 'Name', accessorKey: 'name' },
    {
      header: 'Email',
      accessorKey: 'email',
      cell: ({ row }) => <span className="text-foreground-muted">{row.original.email}</span>,
    },
    {
      header: 'Spaces',
      id: 'spaces',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.spaceIds.length === 0 ? (
            <span className="text-foreground-subtle">None granted</span>
          ) : (
            row.original.spaceIds.map((id) => (
              <Badge key={id} tone="neutral">
                {spaceName(id)}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      id: 'status',
      cell: ({ row }) =>
        !row.original.isActive ? (
          <Badge tone="neutral">Deactivated</Badge>
        ) : row.original.lastLoginAt ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="warning">Invite sent</Badge>
        ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditingGuest(row.original)}>
            Spaces
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resend.mutate(row.original.id)}
            disabled={resend.isPending}
          >
            Resend invite
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-foreground-muted">
          Guests sign in the same way as staff, but see only the channels and boards granted here —
          never the rest of the workspace, and never spaces from any other module. Deactivate a
          guest from the Staff page, the same as any other account.
        </p>
        <Button variant="primary" size="sm" onClick={() => setInviting(true)}>
          <UserPlus /> Invite guest
        </Button>
      </div>
      <DataTable
        data={guests ?? []}
        columns={columns}
        isLoading={isLoading}
        empty={
          <EmptyState
            icon={UserPlus}
            title="No guests invited"
            description="Invite an outside collaborator to specific channels or boards."
            action={{ label: 'Invite guest', onClick: () => setInviting(true) }}
          />
        }
      />
      <InviteGuestDialog open={inviting} onOpenChange={setInviting} spaces={spaces ?? []} />
      <EditGuestSpacesDialog
        guest={editingGuest}
        spaces={spaces ?? []}
        onClose={() => setEditingGuest(null)}
      />
    </div>
  )
}

function InviteGuestDialog({
  open,
  onOpenChange,
  spaces,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  spaces: WorkspaceSpace[]
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [spaceIds, setSpaceIds] = useState<Set<string>>(new Set())

  const reset = () => {
    setEmail('')
    setName('')
    setSpaceIds(new Set())
  }

  const invite = useMutation({
    mutationFn: () =>
      api.post<WorkspaceGuest>('/workspace/guests', { email, name, spaceIds: [...spaceIds] }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.guests() })
      toast.success('Guest invited — an email was sent with a link to set their password')
      onOpenChange(false)
      reset()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not invite the guest',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a guest</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Email" required>
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label="Channels and boards they can see"
            hint="They'll see only these, never the full workspace."
          >
            {() => (
              <SpaceCheckboxList
                spaces={spaces}
                selected={spaceIds}
                onToggle={(id) => setSpaceIds((prev) => toggleInSet(prev, id))}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => invite.mutate()}
            disabled={invite.isPending || !email.trim() || !name.trim()}
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditGuestSpacesDialog({
  guest,
  spaces,
  onClose,
}: {
  guest: WorkspaceGuest | null
  spaces: WorkspaceSpace[]
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [spaceIds, setSpaceIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    setSpaceIds(new Set(guest?.spaceIds ?? []))
  }, [guest])

  const save = useMutation({
    mutationFn: () => api.put(`/workspace/guests/${guest?.id}/spaces`, { spaceIds: [...spaceIds] }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.guests() })
      toast.success('Spaces updated')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={guest !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Spaces for {guest?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <SpaceCheckboxList
            spaces={spaces}
            selected={spaceIds}
            onToggle={(id) => setSpaceIds((prev) => toggleInSet(prev, id))}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
